<script setup lang="ts">
/**
 * Шторка уведомлений справа. Живёт в каркасе, а не в тулбаре: тулбар рисует
 * каждый экран сам, и панель пришлось бы повторить пятнадцать раз.
 */
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { BellOff, X } from 'lucide-vue-next'

import NotificationItem from './NotificationItem.vue'
import { openNotificationTarget } from './route'
import { useNotificationsStore } from './store'
import type { Notification } from './types'

const { t } = useI18n()
const router = useRouter()
const store = useNotificationsStore()

const onItemClick = (notification: Notification) => {
  void store.markRead(notification.id)

  if (openNotificationTarget(notification, router)) {
    store.closePanel()
  }
}

/** Догрузка за сто пикселей до низа — до края докручивают редко. */
const onScroll = (event: Event) => {
  const el = event.target as HTMLElement

  if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
    void store.loadMore()
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="store.isPanelOpen" class="overlay" @click.self="store.closePanel()">
        <Transition name="slide">
          <aside
            v-if="store.isPanelOpen"
            class="panel"
            role="dialog"
            :aria-label="t('notifications.title')"
          >
            <header class="panel__header">
              <h2 class="panel__title">
                {{ t('notifications.title') }}
                <span v-if="store.unreadCount > 0" class="panel__badge">
                  {{ store.unreadCount }}
                </span>
              </h2>

              <div class="panel__actions">
                <button
                  v-if="store.unreadCount > 0"
                  type="button"
                  class="panel__action"
                  @click="store.markAllRead()"
                >
                  {{ t('notifications.markAllRead') }}
                </button>

                <button
                  type="button"
                  class="panel__close"
                  :aria-label="t('notifications.close')"
                  @click="store.closePanel()"
                >
                  <X :size="18" />
                </button>
              </div>
            </header>

            <div class="panel__list" @scroll="onScroll">
              <div v-if="store.isLoading && store.items.length === 0" class="panel__state">
                {{ t('notifications.loading') }}
              </div>

              <div v-else-if="store.items.length === 0" class="panel__state">
                <BellOff :size="32" />
                <span>{{ t('notifications.empty') }}</span>
              </div>

              <template v-else>
                <NotificationItem
                  v-for="item in store.items"
                  :key="item.id"
                  :notification="item"
                  @click="onItemClick"
                />

                <div v-if="store.isLoading" class="panel__state panel__state--more">
                  {{ t('notifications.loading') }}
                </div>
              </template>
            </div>
          </aside>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background-color: rgb(9 9 11 / 20%);
}

.panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 61;
  display: flex;
  flex-direction: column;
  width: 380px;
  max-width: 100vw;
  border-left: 1px solid var(--color-border);
  background-color: var(--color-surface);
}

.panel__header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-border);
}

.panel__title {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: 600;
}

.panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background-color: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: var(--font-size-caption);
  font-weight: 600;
}

.panel__actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.panel__action {
  padding: 4px 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.panel__action:hover {
  background-color: var(--color-primary-tint);
}

.panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.panel__close:hover {
  background-color: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.panel__list {
  flex: 1;
  overflow-y: auto;
}

.panel__state {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.panel__state--more {
  padding: 16px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-leave-active {
  transition: transform 0.2s ease-in;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
